import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";

import AthleteCard from "../../components/AthleteCard";
import Button from "../../components/Button";
import AdminNavBar from "../admin/components/AdminNavBar";

import { getAthletes } from "./api/athleteRequests";
import { useTranslation } from "../../i18n/TranslationProvider";


const ADMIN_ROLES = ["Administrator", "Athletic Director/Administrator"];


const Athletes = () => {
    const { userId, userProfile } = useAuth();
    const navigate = useNavigate();
    const [athletes, setAthletes] = useState([]);
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(false);
    const { t } = useTranslation();


    const isAdmin = ADMIN_ROLES.includes(userProfile?.user_type);



    useEffect(() => {
        const fetchAthletes = async () => {
            setLoading(true);
            try {
                const res = await getAthletes(userId);
                setAthletes(res.data.athletes);
            } catch (err) {
                console.error("Error fetching athletes", err);
            } finally {
                setLoading(false);
            }
        };

        fetchAthletes();
    }, [userId]);

    const filtered = athletes.filter((a) =>
        `${a.athlete_info.first_name} ${a.athlete_info.last_name}`
            .toLowerCase()
            .includes(search.toLowerCase())
    );

    if (loading) {
        return <h6 className="text-center text-lg font-normal">{t("common.loading")}</h6>;
    }
    return (
        <>
            {isAdmin && (
                <AdminNavBar />
            )}
            {/* don't round the left corner if the admin navbar is displayed */}
            <div
                className={`custom-container mx-auto w-4/5 md:w-2/3 lg:w-1/2  ${
                    isAdmin ? "!rounded-tl-none" : "rounded-t-lg"
                }`}
            >
                <div className="text-center mx-auto mb-2">
                    <h2 className="text-xl md:text-3xl font-semibold">
                        {t("athletes.title")}
                    </h2>
                    <p className="italic mt-3 mb-6">
                        <b>{t("athletes.howItWorksLabel")}</b>{" "}
                        {t("athletes.howItWorksDescription")}
                    </p>

                    <h4 className="text-lg md:text-2xl font-semibold">
                        {t("athletes.rosterHeading")}
                    </h4>
                </div>
                {/* Search bar */}
                <div className="relative">
                    <input
                        type="text"
                        value={search}
                        className="block text-sm md:text-base w-full border rounded-lg border-primary px-3 py-2 mb-4 bg-secondary focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t("athletes.searchPlaceholder")}
                    />
                    {/* X icon displays when searching. When clicked, it clears the search */}
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch("")}
                            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke-width="1.5"
                                stroke="currentColor"
                                class="size-6"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M6 18 18 6M6 6l12 12"
                                />
                            </svg>
                        </button>
                    )}
                </div>
                {filtered.length === 0 ? (
                    <h6 className="text-center text-gray-500 my-12">
                        {t("athletes.noAthletesFound")}{" "}
                        <div>
                            <Link
                                to="/add-new-athletes"
                                className="text-teal-600 hover:underline hover:text-teal-700"
                            >
                                {t("athletes.clickToAddAthlete")}
                            </Link>
                        </div>
                    </h6>
                ) : (
                    <div className="space-y-4 overflow-y-auto max-h-96 border-2 p-4 border-gray-200 rounded-lg mb-4">
                        {filtered.map((athlete) => (
                            <AthleteCard
                                key={athlete.athlete_id}
                                athlete={athlete}
                                onClick={() =>
                                    navigate("/iiss-flow", {
                                        state: { athlete },
                                    })
                                }
                                showSubtleText={true}
                            />
                        ))}
                    </div>
                )}
                <Button
                    className="w-full"
                    onClick={() => navigate("/add-new-athletes")}
                >
                    {t("athletes.addNewHeading")}
                </Button>
            </div>
        </>
    );
};

export default Athletes;